/**
 * Scenario Comparison Table - Business Value Calculator
 * Sprint 7: Day 3
 *
 * Displays 3 scenarios for sensitivity analysis:
 * - Best Case: +20% benefits, -10% costs
 * - Base Case: As entered
 * - Worst Case: -20% benefits, +10% costs
 *
 * Each scenario shows NPV, IRR, ROI, and assigned probability
 * Expected Value calculated as weighted average
 */

import React from 'react';
import { FinancialMetrics } from './ExecutiveSummaryCard';

export interface Scenario {
  name: string;
  description: string;
  probability: number;  // 0.0 to 1.0
  metrics: FinancialMetrics;
}

interface ScenarioComparisonTableProps {
  bestCase: Scenario;
  baseCase: Scenario;
  worstCase: Scenario;
}

export const ScenarioComparisonTable: React.FC<ScenarioComparisonTableProps> = ({
  bestCase,
  baseCase,
  worstCase
}) => {
  // Calculate expected value (probability-weighted average)
  const expectedNPV =
    bestCase.metrics.npv * bestCase.probability +
    baseCase.metrics.npv * baseCase.probability +
    worstCase.metrics.npv * worstCase.probability;

  const expectedIRR =
    bestCase.metrics.irr * bestCase.probability +
    baseCase.metrics.irr * baseCase.probability +
    worstCase.metrics.irr * worstCase.probability;

  const expectedROI =
    bestCase.metrics.roi * bestCase.probability +
    baseCase.metrics.roi * baseCase.probability +
    worstCase.metrics.roi * worstCase.probability;

  // Format percentage
  const formatPct = (value: number) => `${(value * 100).toFixed(0)}%`;

  // Format currency
  const formatCurrency = (value: number) => {
    const prefix = value >= 0 ? '$' : '-$';
    return `${prefix}${Math.abs(value).toLocaleString()}`;
  };

  // Get color for metric value
  const getColor = (value: number, isPercentage: boolean = false) => {
    if (value >= (isPercentage ? 50 : 100000)) return '#48bb78'; // Green
    if (value >= 0) return '#9ae6b4'; // Light green
    return '#fc8181'; // Red
  };

  return (
    <div className="scenario-comparison-table">
      <div className="scenario-header">
        <h3 className="scenario-title">Scenario Analysis</h3>
        <p className="scenario-subtitle">
          Sensitivity analysis with Best/Base/Worst cases and expected value
        </p>
      </div>

      <div className="scenario-table-container">
        <table className="scenario-table">
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Scenario</th>
              <th style={{ textAlign: 'center' }}>Probability</th>
              <th style={{ textAlign: 'right' }}>NPV</th>
              <th style={{ textAlign: 'right' }}>IRR</th>
              <th style={{ textAlign: 'right' }}>ROI</th>
            </tr>
          </thead>
          <tbody>
            {/* Best Case */}
            <tr className="scenario-row scenario-row-best">
              <td>
                <div className="scenario-name">
                  <div className="scenario-badge scenario-badge-best">Best</div>
                  <div>
                    <strong>Best Case</strong>
                    <div className="scenario-description">{bestCase.description}</div>
                  </div>
                </div>
              </td>
              <td style={{ textAlign: 'center' }}>
                <span className="scenario-probability">{formatPct(bestCase.probability)}</span>
              </td>
              <td style={{ textAlign: 'right', color: getColor(bestCase.metrics.npv) }}>
                <strong>{formatCurrency(bestCase.metrics.npv)}</strong>
              </td>
              <td style={{ textAlign: 'right', color: getColor(bestCase.metrics.irr, true) }}>
                <strong>{bestCase.metrics.irr.toFixed(1)}%</strong>
              </td>
              <td style={{ textAlign: 'right', color: getColor(bestCase.metrics.roi, true) }}>
                <strong>{bestCase.metrics.roi >= 0 ? '+' : ''}{bestCase.metrics.roi.toFixed(0)}%</strong>
              </td>
            </tr>

            {/* Base Case */}
            <tr className="scenario-row scenario-row-base">
              <td>
                <div className="scenario-name">
                  <div className="scenario-badge scenario-badge-base">Base</div>
                  <div>
                    <strong>Base Case</strong>
                    <div className="scenario-description">{baseCase.description}</div>
                  </div>
                </div>
              </td>
              <td style={{ textAlign: 'center' }}>
                <span className="scenario-probability">{formatPct(baseCase.probability)}</span>
              </td>
              <td style={{ textAlign: 'right', color: getColor(baseCase.metrics.npv) }}>
                <strong>{formatCurrency(baseCase.metrics.npv)}</strong>
              </td>
              <td style={{ textAlign: 'right', color: getColor(baseCase.metrics.irr, true) }}>
                <strong>{baseCase.metrics.irr.toFixed(1)}%</strong>
              </td>
              <td style={{ textAlign: 'right', color: getColor(baseCase.metrics.roi, true) }}>
                <strong>{baseCase.metrics.roi >= 0 ? '+' : ''}{baseCase.metrics.roi.toFixed(0)}%</strong>
              </td>
            </tr>

            {/* Worst Case */}
            <tr className="scenario-row scenario-row-worst">
              <td>
                <div className="scenario-name">
                  <div className="scenario-badge scenario-badge-worst">Worst</div>
                  <div>
                    <strong>Worst Case</strong>
                    <div className="scenario-description">{worstCase.description}</div>
                  </div>
                </div>
              </td>
              <td style={{ textAlign: 'center' }}>
                <span className="scenario-probability">{formatPct(worstCase.probability)}</span>
              </td>
              <td style={{ textAlign: 'right', color: getColor(worstCase.metrics.npv) }}>
                <strong>{formatCurrency(worstCase.metrics.npv)}</strong>
              </td>
              <td style={{ textAlign: 'right', color: getColor(worstCase.metrics.irr, true) }}>
                <strong>{worstCase.metrics.irr.toFixed(1)}%</strong>
              </td>
              <td style={{ textAlign: 'right', color: getColor(worstCase.metrics.roi, true) }}>
                <strong>{worstCase.metrics.roi >= 0 ? '+' : ''}{worstCase.metrics.roi.toFixed(0)}%</strong>
              </td>
            </tr>
          </tbody>
          <tfoot>
            {/* Expected Value */}
            <tr className="scenario-row-expected">
              <td colSpan={2} style={{ textAlign: 'left', paddingLeft: '1rem' }}>
                <strong>Expected Value</strong>
                <div className="scenario-description" style={{ marginTop: '0.25rem' }}>
                  Probability-weighted average
                </div>
              </td>
              <td style={{ textAlign: 'right', color: getColor(expectedNPV) }}>
                <strong style={{ fontSize: '1.125rem' }}>{formatCurrency(expectedNPV)}</strong>
              </td>
              <td style={{ textAlign: 'right', color: getColor(expectedIRR, true) }}>
                <strong style={{ fontSize: '1.125rem' }}>{expectedIRR.toFixed(1)}%</strong>
              </td>
              <td style={{ textAlign: 'right', color: getColor(expectedROI, true) }}>
                <strong style={{ fontSize: '1.125rem' }}>{expectedROI >= 0 ? '+' : ''}{expectedROI.toFixed(0)}%</strong>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="scenario-help">
        <div className="scenario-help-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        </div>
        <div className="scenario-help-content">
          <strong>How to interpret scenario analysis:</strong>
          <ul className="scenario-help-list">
            <li><strong>Best Case (20%):</strong> Optimistic scenario with higher benefits and lower costs than planned</li>
            <li><strong>Base Case (60%):</strong> Most likely scenario based on your current estimates</li>
            <li><strong>Worst Case (20%):</strong> Conservative scenario with lower benefits and higher costs</li>
            <li><strong>Expected Value:</strong> Weighted average considering all scenarios and their probabilities</li>
          </ul>
        </div>
      </div>

      <style>{`
        .scenario-comparison-table {
          background: #ffffff;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          padding: 2rem;
          margin-bottom: 2rem;
        }

        .scenario-header {
          margin-bottom: 1.5rem;
        }

        .scenario-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #2d3748;
          margin-bottom: 0.5rem;
        }

        .scenario-subtitle {
          font-size: 0.875rem;
          color: #718096;
          margin: 0;
        }

        .scenario-table-container {
          overflow-x: auto;
          margin-bottom: 1.5rem;
        }

        .scenario-table {
          width: 100%;
          border-collapse: collapse;
        }

        .scenario-table thead th {
          padding: 0.75rem 1rem;
          font-size: 0.75rem;
          font-weight: 700;
          color: #718096;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          background: #f7fafc;
          border-bottom: 2px solid #e2e8f0;
        }

        .scenario-row td {
          padding: 1rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .scenario-row:hover {
          background: #f7fafc;
        }

        .scenario-row-best {
          background: rgba(72, 187, 120, 0.05);
        }

        .scenario-row-base {
          background: rgba(0, 148, 171, 0.05);
        }

        .scenario-row-worst {
          background: rgba(252, 129, 129, 0.05);
        }

        .scenario-row-expected {
          border-top: 2px solid #2d3748;
          background: linear-gradient(135deg, rgba(0, 148, 171, 0.1) 0%, rgba(0, 71, 114, 0.1) 100%);
        }

        .scenario-row-expected td {
          padding: 1.25rem 1rem;
          border-bottom: none;
        }

        .scenario-name {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .scenario-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 60px;
          padding: 0.375rem 0.75rem;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-radius: 6px;
          color: #ffffff;
        }

        .scenario-badge-best {
          background: #48bb78;
        }

        .scenario-badge-base {
          background: #0094ab;
        }

        .scenario-badge-worst {
          background: #fc8181;
        }

        .scenario-description {
          font-size: 0.75rem;
          color: #a0aec0;
          margin-top: 0.25rem;
        }

        .scenario-probability {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.375rem 0.75rem;
          font-size: 0.875rem;
          font-weight: 700;
          background: #f7fafc;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
        }

        .scenario-help {
          display: flex;
          gap: 1rem;
          padding: 1.5rem;
          background: linear-gradient(135deg, rgba(0, 148, 171, 0.05) 0%, rgba(0, 71, 114, 0.05) 100%);
          border: 1px solid rgba(0, 148, 171, 0.2);
          border-radius: 8px;
        }

        .scenario-help-icon {
          flex-shrink: 0;
          color: #0094ab;
        }

        .scenario-help-content {
          font-size: 0.875rem;
          color: #4a5568;
          line-height: 1.6;
        }

        .scenario-help-list {
          margin: 0.75rem 0 0 1.5rem;
          padding: 0;
        }

        .scenario-help-list li {
          margin-bottom: 0.5rem;
        }

        /* Responsive Design */
        @media (max-width: 968px) {
          .scenario-table {
            font-size: 0.875rem;
          }

          .scenario-name {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5rem;
          }
        }
      `}</style>
    </div>
  );
};

// Helper function to generate scenarios from base metrics
export const generateScenarios = (
  baseBenefits: number,
  baseCosts: number,
  baseMetrics: FinancialMetrics
): { bestCase: Scenario; baseCase: Scenario; worstCase: Scenario } => {
  // Calculate best case metrics (simplified - would need actual recalculation)
  const bestCase: Scenario = {
    name: 'Best Case',
    description: '+20% benefits, -10% costs',
    probability: 0.2,
    metrics: {
      npv: baseMetrics.npv * 1.4,  // Rough approximation
      irr: baseMetrics.irr * 1.3,
      roi: baseMetrics.roi * 1.4,
      paybackMonths: baseMetrics.paybackMonths !== null ? baseMetrics.paybackMonths * 0.7 : null
    }
  };

  // Base case
  const baseCase: Scenario = {
    name: 'Base Case',
    description: 'As entered',
    probability: 0.6,
    metrics: baseMetrics
  };

  // Calculate worst case metrics
  const worstCase: Scenario = {
    name: 'Worst Case',
    description: '-20% benefits, +10% costs',
    probability: 0.2,
    metrics: {
      npv: baseMetrics.npv * 0.6,  // Rough approximation
      irr: baseMetrics.irr * 0.7,
      roi: baseMetrics.roi * 0.6,
      paybackMonths: baseMetrics.paybackMonths !== null ? baseMetrics.paybackMonths * 1.3 : null
    }
  };

  return { bestCase, baseCase, worstCase };
};

export default ScenarioComparisonTable;
